import type * as React from 'react';
import { DataTable, EmptyState, useListQuery, type DataTableColumn, type DataTableTexts } from '@redhand/ui';
import { demoProperties, queryDemoProperties, type DemoProperty } from '../demo-data.js';
import { PageIntro, StateSection } from '../ui-helpers.js';

const columns: readonly DataTableColumn<DemoProperty>[] = [
  { key: 'id', header: 'Číslo' },
  { key: 'name', header: 'Nemovitost', sortable: true },
  { key: 'client', header: 'Klient', sortable: true },
  { key: 'status', header: 'Stav', render: (r) => <span className="rh-badge rh-badge--neutral">{r.status}</span> },
  { key: 'price', header: 'Cena', align: 'right', sortable: true },
];

const texts: DataTableTexts = {
  paginationLabel: 'Stránkování',
  pageStatus: (page, pageCount, total) => `Stránka ${page} z ${pageCount} (celkem ${total} řádků)`,
  prev: 'Předchozí',
  next: 'Další',
  loading: 'Načítám seznam…',
  errorTitle: 'Načtení seznamu selhalo',
  retry: 'Zkusit znovu',
  exportCsv: 'Export CSV',
};

const ALL = demoProperties();

/** Živá ukázka: useListQuery + list-kontrakt nad demo daty ("server" je lokální). */
function LiveTable(): React.JSX.Element {
  const { query, setQuery, data, isLoading } = useListQuery<DemoProperty>({
    queryKey: ['gallery', 'properties'],
    fetcher: (q) => Promise.resolve(queryDemoProperties(ALL, q)),
  });
  return (
    <DataTable<DemoProperty>
      caption="Nemovitosti — 412 řádků demo dat"
      columns={columns}
      rowKey={(r) => r.id}
      {...(data !== undefined ? { data } : {})}
      loading={isLoading}
      query={query}
      onQueryChange={setQuery}
      onExport={() => {}}
      emptyState={<EmptyState title="Žádné řádky" />}
      texts={texts}
    />
  );
}

export function DataTablePage(): React.JSX.Element {
  return (
    <div>
      <PageIntro
        title="DataTable + useListQuery"
        lead="Řízená list-kontraktem (packages/ui/docs/LIST-CONTRACT.md): řazení, stránkování a export dělá vždy server; tady ho simulují demo data (412 řádků, dlouhé adresy, klient bez příjmení)."
      />
      <StateSection title="Plný stav — 412 řádků, klikací řazení a stránkování">
        <LiveTable />
      </StateSection>
      <StateSection title="Načítání (skeleton)">
        <DataTable<DemoProperty>
          caption="Nemovitosti — načítání"
          columns={columns}
          rowKey={(r) => r.id}
          loading
          query={{ page: 1, pageSize: 50 }}
          onQueryChange={() => {}}
          texts={texts}
        />
      </StateSection>
      <StateSection title="Chyba s možností opakování">
        <DataTable<DemoProperty>
          caption="Nemovitosti — chyba"
          columns={columns}
          rowKey={(r) => r.id}
          error="Server neodpovídá (traceId 018f3c2e-demo). Zkus to znovu, nebo kontaktuj podporu."
          onRetry={() => {}}
          query={{ page: 1, pageSize: 50 }}
          onQueryChange={() => {}}
          texts={texts}
        />
      </StateSection>
      <StateSection title="Prázdný stav">
        <DataTable<DemoProperty>
          caption="Nemovitosti — prázdný stav"
          columns={columns}
          rowKey={(r) => r.id}
          data={{ items: [], total: 0, page: 1, pageSize: 50 }}
          query={{ page: 1, pageSize: 50 }}
          onQueryChange={() => {}}
          emptyState={
            <EmptyState
              icon={<span>🏠</span>}
              title="Zatím žádné nemovitosti"
              description="Začni náborem první nemovitosti — formulář tě provede adresou, parametry a fotkami."
              action={<button type="button" className="rh-btn rh-btn--primary">Nabrat nemovitost</button>}
            />
          }
          texts={texts}
        />
      </StateSection>
    </div>
  );
}
